import { Route } from '@angular/router';

import { BASE_URL } from '@cer/cer-session/cer-session-environment.service';

import { CerLink } from '@cer/cer-markdown/cer-link';

import { CerFormView } from '@form/cer-form-view';
import { CerFormContext } from '@form/cer-form-context';
import { CerFormDataNavigation } from '@form/cer-form-navigation.service';
import { CerFormPaneType } from '@form/cer-form-panes/cer-form-panes.service';

import { DocumentLibDto } from '@app/api';
import { documentTypeByExtension, fileNameExtensionGet } from '@cer/cer-form/cer-form-view-type/cer-form-view-type-document/cer-form-view-document-type';

export const folderNameDefault = '/';

export class CerAppDocumentationLinkHelper {

  constructor(
    private context: CerFormContext, private docLib: DocumentLibDto,
    private parentView: CerFormView, private paneTarget: CerFormPaneType) {
  }

  private navSvc = this.context.form.formNavigationService;
  private ctxSvc = this.context.form.formContextService;
  private baseUrl = this.context.injector.get(BASE_URL);

  private error(msg : string , url: string = null) : string {
    this.context.ui.error(msg, "Dokumentation: " + url);
    return msg;
  }

  private toast(msg: string, url: string = null) {
    this.context.ui.error(msg, "Dokumentation: " + url);
  }

  public redirectLink(link: CerLink) : void {
    if (!(link.url?.length > 0)) {
      this.error("Ingen link adresse er angivet", "");
      return;
    }

    if (link.protocol == 'http') {
      window.open(link.url, '_blank')?.focus();
      return;
    }

    var nav: CerFormDataNavigation = null;

    // Try to determine route
    var route = this.navSvc.routeGetByUrl(link.url);
    link.title ??= <string>route?.title;

    if (link.protocol == 'app') {
      // Navigate to application view
      if (route == null) {
        this.toast(`Menupunkt '${link.url}' findes ikke`, link.url);
        link.url = 'doc/'+link.url;
      }
      else {
        nav = this.navigationGet(link, route);
      }
    }

    if (nav == null) {
      // Navigate to documentation view
      route = this.navSvc.routeGetByUrl('help');
      if (route == null) {
        throw (this.error("Dokumentation kan ikke åbnes", "/help"));
      }
      link.title ??= <string>route.title;
      const attachmentUrl = this.linkedAttachmentUrlGet(link.url);
      const callerData = { 
        documentFileName: link.url, 
        documentType: documentTypeByExtension(fileNameExtensionGet(link.url)), 
        documentUrl: attachmentUrl }
      nav = this.navigationGet(link, route, callerData);
    }
    if (nav != null) {
      this.ctxSvc.navigate(nav);
    }
  }

  private navigationGet(link: CerLink, route: Route, callerData: any = null): CerFormDataNavigation {
    // Pass document as caller data on navigation
    const nav = new CerFormDataNavigation();
    nav.route = route;
    nav.url = route.path;
    nav.callerData = callerData;
    nav.parentView = this.parentView;
    nav.paneTarget = this.paneTarget;
    nav.title = link.title;
    return nav;
  }

  public linkedAttachmentUrlGet(url: string): string {
    const getUrl = linkedAttachmentUrlByDocLibGet(
      this.baseUrl,
      url,
      this.docLib
    );
    return getUrl;
  }

  public linkedAttachmentUploadUrlGet(folderName: string, overwriteExisting: boolean = true, getList: boolean = false): string {
    const uploadUrl = linkedAttachmentUploadUrlByDocLibGet(this.baseUrl, this.docLib, folderName ?? '/', overwriteExisting, getList);
    return uploadUrl;
  }
}

function markddownTitleGet(markdown: string): string {
  const exp = '^# (.*)[\r\n]+';
  const regEx = new RegExp(exp);
  const match = markdown.match(regEx);
  return match?.length > 1 ? match[1] : null;
}

function markdownMergeLinkData(markdown: any, title: string, url: string): string {
  url = url?.startsWith("/") ? url.replace("/", "") : url;
  const appLinkMD = (url?.length > 0 ? `> Link [${title}](app:${url})\r\n` : ``);
  const appLinkReplace = `$1${title}$3${appLinkMD}`;
  const exp = '(^# )({{title}})([\r\n])';
  const regEx = new RegExp(exp, 'g');
  return markdown?.length > 0 ? markdown.replace(regEx, appLinkReplace) : "> Tomt dokument";
}

function linkedAttachmentUrlByDocLibGet(baseUrl: string, fileFullPath: string, docLib: DocumentLibDto): string {
  const fileNameSplits = fileFullPath?.split('/');
  if (fileNameSplits.length > 0) {
    const fileName = fileNameSplits[fileNameSplits.length - 1];
    fileNameSplits.pop();
    const folderName = fileNameSplits.length > 0 ? fileNameSplits.join("/") : "/";

    if (fileName?.length > 0) {
      return linkedAttachmentUrlGet(baseUrl, docLib.linkId.toString(), docLib.id.toString(), fileName, folderName, docLib.token);
    }
  }
  return null;
}

export function urlAddressGet(url: string): string {
  if (url?.includes('?')) {
    const parts = url.split('?');
    if (parts.length > 1) {
      parts.pop();
    }
    return parts.join('?');
  }
  return url;
}

const serviceUrl = "api/datalinkattachment/";
const fileMethod = "file";
const uploadFileMethod = "formupload";

function linkedAttachmentUrlGet(baseUrl: string, entityId: string, id: string, fileName: string, folderName: string, token: string): string {
  var uri: string = baseUrl + serviceUrl + fileMethod + "?";
  if (entityId?.length > 0) {
    uri += "&entityId=" + encodeURI(entityId);
  }
  if (id?.length > 0) {
    uri += "&rowId=" + encodeURI(id);
  }
  if (fileName?.length > 0) {
    uri += "&fileName=" + encodeURI(fileName);
  }
  else {
    return null;
  }
  if (folderName?.length > 0) {
    uri += "&folderName=" + encodeURI(folderName);
  }
  if (token?.length > 0) {
    uri += "&token=" + encodeURI(token);
  }
  return uri;
}

function linkedAttachmentUploadUrlByDocLibGet(baseUrl: string, docLib: DocumentLibDto, folderName: string, overwriteExisting: boolean, getList: boolean): string {
  return linkedAttachmentUploadUrlGet(baseUrl, docLib.linkId.toString(), docLib.id.toString(), folderName, docLib.token, overwriteExisting, getList);
}

function linkedAttachmentUploadUrlGet(baseUrl: string, entity: string, id: string, folderName: string,
  token: string, overwriteExisting: boolean, getList: boolean): string {

  var uri: string = baseUrl + serviceUrl + uploadFileMethod + "?";
  if (entity?.length > 0) {
    uri += "&$entity=" + encodeURI(entity);
  }
  if (id?.length > 0) {
    uri += "&$rowId=" + encodeURI(id);
  }
  if (folderName?.length > 0) {
    uri += "&$folderName=" + encodeURI(folderName);
  }
  if (token?.length > 0) {
    uri += "&$token=" + encodeURI(token);
  }
  uri += "&$overwriteExisting=" + (overwriteExisting == true ? '1' : '0');
  uri += "&$getList" + (getList == true ? '1' : '0');

  return uri;
}
